import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export type LightboxImage = {
  src: string;
  alt: string;
  label?: string;
  caption?: string;
  details?: string[];
};

interface LightboxProps {
  images: LightboxImage[];
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

export function Lightbox({ images, index, onClose, onIndexChange }: LightboxProps) {
  const isOpen = index !== null;

  const next = useCallback(() => {
    if (index === null) return;
    onIndexChange((index + 1) % images.length);
  }, [index, images.length, onIndexChange]);

  const prev = useCallback(() => {
    if (index === null) return;
    onIndexChange((index - 1 + images.length) % images.length);
  }, [index, images.length, onIndexChange]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, next, prev]);

  if (!isOpen || index === null) return null;
  const img = images[index];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={img.alt}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close gallery"
        className="absolute right-4 top-4 z-10 flex h-12 w-12 items-center justify-center bg-white/10 text-white transition hover:bg-white/20"
      >
        <X className="h-6 w-6" />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous image"
            className="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center bg-white/10 text-white transition hover:bg-white/20 sm:h-14 sm:w-14"
          >
            <ChevronLeft className="h-7 w-7" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next image"
            className="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center bg-white/10 text-white transition hover:bg-white/20 sm:h-14 sm:w-14"
          >
            <ChevronRight className="h-7 w-7" />
          </button>
        </>
      )}

      <figure
        className="relative flex max-h-full max-w-7xl flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={img.src}
          alt={img.alt}
          className="max-h-[80vh] w-auto max-w-full object-contain"
        />
        <figcaption className="mt-4 max-w-3xl text-center text-sm text-white/90">
          <div className="font-display text-base uppercase tracking-wider text-white">{img.label ?? img.alt}</div>
          {img.caption && (
            <p className="mt-2 text-sm leading-relaxed text-white/85">{img.caption}</p>
          )}
          {img.details && img.details.length > 0 && (
            <ul className="mx-auto mt-3 grid max-w-2xl gap-1 text-left text-xs text-white/75 sm:grid-cols-2">
              {img.details.map((d) => (
                <li key={d} className="flex items-start gap-2">
                  <span aria-hidden="true" className="mt-1 inline-block h-1.5 w-1.5 shrink-0 bg-red" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          )}
          {images.length > 1 && (
            <div className="mt-3 text-xs text-white/60">
              {index + 1} / {images.length}
            </div>
          )}
        </figcaption>
      </figure>
    </div>
  );
}
